import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { addPricing } from "../../features/pricing/PricingService";
import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Country } from "country-state-city";

const COUNTRIES = Country.getAllCountries();
const TEMPLATE_TYPES = ["Marketing", "Utility", "Authentication"];

export default function AddPricePerMsg() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const [countryCode, setCountryCode] = useState("IN");
    const [templateType, setTemplateType] = useState("Marketing");
    const [baseCost, setBaseCost] = useState("");
    const [sellingPrice, setSellingPrice] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const selectedCountry = Country.getCountryByCode(countryCode);
    const phoneCode = selectedCountry?.phonecode ?? "";
    const currency = selectedCountry?.currency ?? "";

    const handleSubmit = async () => {
        if (!countryCode || !templateType || !baseCost || !sellingPrice) {
            toast.error("Please fill all fields");
            return;
        }

        if (!phoneCode) {
            toast.error("Phone code not available for selected country");
            return;
        }

        if (!currency) {
            toast.error("Currency not available for selected country");
            return;
        }

        setSubmitting(true);
        try {
            await dispatch(
                addPricing({
                    country: selectedCountry?.name ?? countryCode,
                    countryCode,
                    phoneCode,
                    templateType,
                    baseCost: parseFloat(baseCost),
                    sellingPrice: parseFloat(sellingPrice),
                    currency,
                })
            ).unwrap();

            toast.success("Pricing added successfully!");
            navigate(-1);
        } catch (err: any) {
            toast.error(err?.message || "Failed to add pricing");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main transition"
                            onClick={() => navigate(-1)}
                        />
                        <span className="font-bold text-lg uppercase">Add Pricing</span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="bg-white rounded-4xl p-7 flex flex-col gap-8">

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                    {/* Country */}
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Country</label>
                        <select
                            value={countryCode}
                            onChange={e => setCountryCode(e.target.value)}
                            className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                        >
                            {COUNTRIES.map(c => (
                                <option key={c.isoCode} value={c.isoCode}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Template Type */}
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Template Type</label>
                        <select
                            value={templateType}
                            onChange={e => setTemplateType(e.target.value)}
                            className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                        >
                            {TEMPLATE_TYPES.map(t => (
                                <option key={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    {/* Base Cost */}
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Base Cost</label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="Enter base cost"
                            value={baseCost}
                            onChange={e => setBaseCost(e.target.value)}
                            className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                        />
                    </div>

                    {/* Selling Price */}
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Selling Price</label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="Enter selling price"
                            value={sellingPrice}
                            onChange={e => setSellingPrice(e.target.value)}
                            className="border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-brand-main"
                        />
                    </div>

                    {/* Phone Code — auto-filled */}
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Phone Code</label>
                        <input
                            type="text"
                            value={phoneCode ? `+${phoneCode}` : "—"}
                            readOnly
                            className="border rounded-lg px-4 py-2 bg-gray-50 text-gray-500 outline-none cursor-not-allowed"
                        />
                    </div>

                    {/* Currency — auto-filled */}
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Currency</label>
                        <input
                            type="text"
                            value={currency || "—"}
                            readOnly
                            className="border rounded-lg px-4 py-2 bg-gray-50 text-gray-500 outline-none cursor-not-allowed"
                        />
                    </div>

                </div>

                <div className="flex sm:justify-end mt-4 sm:mt-10 w-full">
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full sm:w-auto px-6 py-2 bg-brand-blue-gradient text-white font-semibold text-lg rounded-xl shadow hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {submitting ? "Adding..." : "Add Pricing"}
                    </button>
                </div>

            </div>
        </PageLayout>
    );
}